import { Link } from "react-router-dom";
import type { Business } from "@/types/directory";
import BusinessCard from "@/components/base/BusinessCard";

interface BusinessGridProps {
  businesses: Business[];
  loading?: boolean;
  emptyTitle?: string;
  emptyMessage?: string;
}

function SkeletonCard() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="h-48 w-full animate-pulse bg-background-200" />
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="h-3 w-24 animate-pulse rounded-full bg-background-200" />
        <div className="h-4 w-4/5 animate-pulse rounded-full bg-background-200" />
        <div className="h-3 w-3/5 animate-pulse rounded-full bg-background-200" />
        <div className="mt-auto h-10 w-full animate-pulse rounded-md bg-background-200" />
      </div>
    </div>
  );
}

export default function BusinessGrid({
  businesses,
  loading = false,
  emptyTitle = "No businesses found",
  emptyMessage = "Try a different keyword, category or location to discover more local businesses.",
}: BusinessGridProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-search-eye-line text-2xl" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">{emptyTitle}</h3>
        <p className="mt-2 max-w-md text-sm text-foreground-600">{emptyMessage}</p>
        <Link
          to="/directory"
          className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-refresh-line text-base" />
          </span>
          Reset filters
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {businesses.map((business) => (
        <BusinessCard key={business.id} business={business} />
      ))}
    </div>
  );
}